import React, { useState, useEffect, useRef } from 'react';
import {
  Music,
  Volume2,
  VolumeX,
  Sliders,
  Sparkles,
  Check,
  X,
} from 'lucide-react';
import {
  soundEngine,
  type BackgroundMusicMood,
} from '../utils/soundAndSpeech.ts';

interface BackgroundMusicControlProps {
  userUid?: string;
  compact?: boolean;
  className?: string;
}

const MOOD_OPTIONS: Array<{
  id: BackgroundMusicMood;
  label: string;
  desc: string;
}> = [
  {
    id: 'auto',
    label: 'Auto (Adaptive)',
    desc: 'Default adaptive soundtrack',
  },
  {
    id: 'relaxed',
    label: 'Relaxed Sanctuary',
    desc: 'Peaceful Bible study atmosphere',
  },
  {
    id: 'inspirational',
    label: 'Inspirational',
    desc: 'Motivational and uplifting atmosphere',
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    desc: 'Deep orchestral challenge atmosphere',
  },
  {
    id: 'competitive',
    label: 'Competitive Focus',
    desc: 'Rhythmic competitive gaming atmosphere',
  },
];

export const BackgroundMusicControl: React.FC<BackgroundMusicControlProps> = ({
  userUid,
  compact = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [desktopAlignLeft, setDesktopAlignLeft] = useState(false);
  const [, setTick] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = soundEngine.subscribe(() => {
      setTick((t) => t + 1);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const updateAlignment = () => {
      if (!containerRef.current || typeof window === 'undefined') return;
      const rect = containerRef.current.getBoundingClientRect();
      // If right-anchoring a 320px popover would push its left edge near/off the screen (< 12px),
      // anchor it to the left edge of the trigger on desktop/tablet viewports.
      setDesktopAlignLeft(rect.right < 336);
    };

    updateAlignment();
    window.addEventListener('resize', updateAlignment);
    return () => window.removeEventListener('resize', updateAlignment);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const bgEnabled = soundEngine.bgMusicEnabled;
  const bgMuted = soundEngine.bgMusicMuted;
  const volumePercent = Math.round(soundEngine.bgMusicVolume * 100);
  const currentMood = soundEngine.bgMusicMood;
  const activeTrack = soundEngine.getActiveResolvedMood();
  const sfxEnabled = soundEngine.sfxEnabled;

  const handleToggleMusic = () => {
    soundEngine.setBgMusicEnabled(!bgEnabled, userUid);
  };

  const handleToggleMute = () => {
    soundEngine.setBgMusicMuted(!bgMuted, userUid);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    soundEngine.setBgMusicVolume(val / 100, userUid);
    if (!soundEngine.bgMusicEnabled && val > 0) {
      soundEngine.setBgMusicEnabled(true, userUid);
    }
  };

  return (
    <div ref={containerRef} className={`relative inline-block max-w-full ${className}`}>
      <div className="inline-flex items-center gap-1 rounded-xl border border-[#262626] bg-[#141414] p-1 max-w-full">
        <button
          type="button"
          data-testid="btn-toggle-bg-music"
          onClick={handleToggleMusic}
          title={
            bgEnabled
              ? `Background Music ON (${activeTrack}) — Click to turn OFF`
              : 'Background Music OFF — Click to play'
          }
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
            bgEnabled && !bgMuted
              ? 'bg-[#F97316]/15 text-[#F97316] border border-[#F97316]/40'
              : 'text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#1C1C1C]'
          }`}
        >
          <Music className="w-3.5 h-3.5 shrink-0" />
          {!compact && (
            <span className="whitespace-nowrap">
              {bgEnabled && !bgMuted ? 'Music ON' : 'Music OFF'}
            </span>
          )}
        </button>

        <button
          type="button"
          data-testid="btn-open-audio-mixer"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
          aria-label="Audio & Background Music Settings"
          title="Audio & Background Music Mixer"
          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
            isOpen
              ? 'bg-[#1C1C1C] text-[#F97316]'
              : 'text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#1C1C1C]'
          }`}
        >
          <Sliders className="w-3.5 h-3.5" />
        </button>
      </div>

      {isOpen && (
        <>
          {/* Mobile Backdrop Overlay: prevents accidental clicks on underlying Game Mode cards and allows outside-tap dismissal */}
          <div
            data-testid="audio-mixer-backdrop"
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-[2px] sm:hidden"
          />

          <div
            role="dialog"
            aria-label="Gameplay Audio and Music Settings"
            data-testid="audio-mixer-popover"
            className={`fixed left-3 right-3 top-1/2 -translate-y-1/2 w-auto max-w-[calc(100vw-24px)] max-h-[calc(100dvh-32px)] overflow-y-auto overscroll-contain box-border z-[75] sm:absolute sm:top-full sm:mt-2 sm:translate-y-0 sm:w-80 sm:max-w-[calc(100vw-24px)] sm:max-h-[82vh] sm:z-50 rounded-2xl border border-[#262626] bg-[#141414] p-4 shadow-2xl text-left ${
              desktopAlignLeft
                ? 'sm:left-0 sm:right-auto'
                : 'sm:right-0 sm:left-auto'
            }`}
          >
            {/* Header Row with Active Status & Accessible Close Button */}
            <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-[#262626]">
              <div className="flex items-center gap-2 min-w-0">
                <Music className="w-4 h-4 text-[#F97316] shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#FAFAFA] truncate">
                  Gameplay Audio &amp; Music
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-md bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3]">
                  {bgEnabled ? activeTrack : 'Paused'}
                </span>
                <button
                  type="button"
                  data-testid="btn-close-audio-mixer"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close Audio Settings"
                  title="Close"
                  className="p-1 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Background Music Toggle */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  Background Music
                </div>
                <div className="text-[11px] text-[#737373]">
                  Non-distracting instrumental score
                </div>
              </div>
              <button
                type="button"
                onClick={handleToggleMusic}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer ${
                  bgEnabled
                    ? 'bg-[#F97316] text-white'
                    : 'bg-[#1C1C1C] border border-[#404040] text-[#A3A3A3]'
                }`}
              >
                {bgEnabled ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Volume Slider + Mute */}
            <div className="mb-4 p-3 rounded-xl bg-[#1C1C1C] border border-[#262626]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[#D4D4D4]">
                  Music Volume
                </span>
                <span className="text-xs font-mono font-bold tabular-nums text-[#FAFAFA]">
                  {bgMuted ? 'Muted' : `${volumePercent}%`}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleToggleMute}
                  aria-label={bgMuted ? 'Unmute music' : 'Mute music'}
                  className="p-1.5 rounded-lg bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-[#FAFAFA] shrink-0 cursor-pointer"
                >
                  {bgMuted || volumePercent === 0 ? (
                    <VolumeX className="w-3.5 h-3.5 text-rose-400" />
                  ) : (
                    <Volume2 className="w-3.5 h-3.5 text-[#10B981]" />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={bgMuted ? 0 : volumePercent}
                  onChange={handleVolumeChange}
                  className="w-full accent-[#F97316] cursor-pointer"
                />
              </div>
            </div>

            {/* Mood Selector */}
            <div className="mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[#F97316] shrink-0" />
                <span>Instrumental Mood</span>
              </div>
              <div className="space-y-1.5">
                {MOOD_OPTIONS.map((m) => {
                  const active = currentMood === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => soundEngine.setBgMusicMood(m.id, userUid)}
                      className={`w-full text-left px-3 py-2 rounded-xl border transition-all flex items-center justify-between gap-2 cursor-pointer ${
                        active
                          ? 'bg-[#F97316]/10 border-[#F97316]/50 text-[#FAFAFA]'
                          : 'bg-[#1C1C1C] border-[#262626] text-[#A3A3A3] hover:border-[#404040]'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-[#FAFAFA] truncate">
                          {m.label}
                        </div>
                        <div className="text-[10px] text-[#737373] truncate">
                          {m.desc}
                        </div>
                      </div>
                      {active && (
                        <Check className="w-3.5 h-3.5 text-[#F97316] shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sound Effects Toggle (Separate from Background Music) */}
            <div className="pt-3 border-t border-[#262626] flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#FAFAFA]">
                  Sound Effects (SFX)
                </div>
                <div className="text-[10px] text-[#737373]">
                  Answer chimes &amp; countdown ticks
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  soundEngine.sfxEnabled = !sfxEnabled;
                  soundEngine.saveToLocalStorage(userUid);
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer ${
                  sfxEnabled
                    ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
                    : 'bg-[#1C1C1C] border border-[#404040] text-[#737373]'
                }`}
              >
                {sfxEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
